import React, { FC, useContext, useEffect, useMemo, useState } from 'react'

import { VespaRecommendationConfiguration } from '@pushly/models/lib/structs/journeys'
import { RelativeDate } from '@pushly/models/lib/structs/relative-date'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { VespaRecommendedNotificationContext, VespaRecommendedNotificationDispatchContext } from '../reducer'
import { VespaRankingProfile } from '@pushly/models/lib/enums/recommendations/vespa/vespa-ranking-profile'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { EditableViewContext } from '@pushly/aqe/lib/components/hoc/editable-view/editable-view-context'
import {
    PQueryBuilder,
    PQueryBuilderField,
    PQueryBuilderProvider,
    Well,
    usePQueryBuilder,
} from '@pushly/aqe/lib/components'
import { Form, Popover } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { PQueryDocument } from '@pushly/models/lib/interfaces/pquery-document'
import { PropertiesOf } from '@pushly/models/lib/types'
import {
    VespaStrategyPresets,
    STRATEGY_PRESETS,
    StrategyPreset,
    StrategySelection,
    RankingWeights,
    CUSTOM_STRATEGY,
} from './vespa-strategy-presets'
import { VespaRankingWeights } from './vespa-ranking-weights'
import { VespaConfigurationCard } from './vespa-configuration-card'
import { VespaConfigSummary, generateSummaryText } from './vespa-config-summary'
import {
    VespaContentWindow,
    ContentWindow,
    ContentWindowSelection,
    contentWindowToSeconds,
    secondsToContentWindow,
    detectContentWindowPreset,
} from './vespa-content-window'
import { CUSTOM_PRESET, detectPreset as detectPresetMatch } from './sticky-preset'
import { EXCLUSION_OPERATORS, INCLUSION_OPERATORS, MAX_VESPA_FILTERS, VESPA_FILTER_FIELDS } from '../filters/types'

type VespaConfigurationEditorProps = {
    configuration: { recordId: string; configuration: VespaRecommendationConfiguration }
    handleAdd: (config?: VespaRecommendationConfiguration) => void
}

/**
 * Maps a dial stop (0-4) to the actual coefficient sent to Vespa as
 * `input.query(personalized_weight)` / `input.query(recent_weight)`.
 *
 * Non-uniform on purpose — RRF's reciprocal_rank() terms cap out at parity
 * with each other, so a naive 0-1 dial range would let "Max" reach parity
 * with a single always-on base term (text_score/signal_score) but never
 * exceed it. Stops map to 0/0.5/1.0/1.5/2.0 instead: "Med" (stop 2) is
 * parity with one base term — matching the old implicit always-on-hybrid
 * default — and "Max" (stop 4) can meaningfully outweigh it. A wider ceiling
 * was avoided because semantic_closeness's flat score curve within a single
 * publisher's content makes a very high ceiling more likely to promote noise
 * than a confident personalization signal.
 */
const DIAL_STOP_WEIGHTS = [0, 0.5, 1.0, 1.5, 2.0] as const

export function dialStopToWeight(dialStop: number): number {
    return DIAL_STOP_WEIGHTS[dialStop] ?? 1.0
}

/**
 * Reverse of {@link dialStopToWeight} — snaps a stored weight value to the
 * nearest dial stop for display. Snapping (rather than requiring an exact
 * match) means a weight set outside the UI (direct API call, future preset
 * change) still renders as *some* dial position rather than falling through
 * to a fallback/undefined state.
 */
export function weightToDialStop(weight: number): number {
    let closestStop = 0
    let closestDistance = Math.abs(weight - DIAL_STOP_WEIGHTS[0])
    for (let stop = 1; stop < DIAL_STOP_WEIGHTS.length; stop++) {
        const distance = Math.abs(weight - DIAL_STOP_WEIGHTS[stop])
        if (distance < closestDistance) {
            closestDistance = distance
            closestStop = stop
        }
    }
    return closestStop
}

/**
 * Every dial-driven configuration uses the hybrid rank profile.
 * "Personalized: Off" is handled by a retrieval-cost fast path in the
 * recommendation query builder (it skips the nearestNeighbor clause entirely
 * when personalized_weight is 0) rather than by swapping to the heuristic
 * profile. This keeps a single, continuous ranking formula across the whole
 * dial range instead of a formula discontinuity at the old profile-swap
 * boundary.
 *
 * `ranking_profile` remains an independently-settable field on the stored
 * config (not derived/removed) so a caller outside this dial UI can still
 * explicitly request heuristic or a future profile — this function only
 * governs what the dial UI itself writes.
 */
export function weightsToProfile(_weights: RankingWeights): VespaRankingProfile {
    return VespaRankingProfile.HYBRID
}

/**
 * Reverse-maps a legacy saved config (no personalized_weight/recent_weight)
 * back to dial weights, using the old ranking_profile/freshness_half_response
 * proxies. Kept for back-compat only — see {@link profileToWeights}.
 */
function legacyProfileToWeights(config: VespaRecommendationConfiguration): RankingWeights {
    const personalized = config.ranking_profile === VespaRankingProfile.HYBRID ? 4 : 1

    const halfResponse = config.freshness_half_response?.intervalSeconds ?? null
    let recent = 1
    if (halfResponse === null) recent = 0
    else if (halfResponse >= 86400) recent = 1
    else if (halfResponse >= 43200) recent = 2
    else if (halfResponse >= 21600) recent = 3
    else recent = 4

    return { personalized, recent }
}

/**
 * Reverse-maps the stored ranking config back to UI dial weights for display.
 *
 * Prefers the real, exact personalized_weight/recent_weight fields when
 * present — this is what fixes the old Balanced-preset round-trip bug:
 * previously personalized/recent were derived from lossy proxies
 * (ranking_profile could only distinguish 2 states; freshness_half_response
 * buckets collapsed distinct dial values), so a saved Balanced config
 * ({personalized: 2, recent: 2}) read back as personalized: 4 (any HYBRID
 * config was indistinguishable from Personalized/Max). Storing the actual
 * weight makes this an exact, lossless round-trip via {@link weightToDialStop}.
 *
 * Falls back to {@link legacyProfileToWeights} for configs saved before this
 * field existed (personalized_weight/recent_weight both null/undefined).
 */
export function profileToWeights(config: VespaRecommendationConfiguration): RankingWeights {
    const legacy = legacyProfileToWeights(config)

    const personalized =
        config.personalized_weight != null ? weightToDialStop(config.personalized_weight) : legacy.personalized
    const recent = config.recent_weight != null ? weightToDialStop(config.recent_weight) : legacy.recent

    return { personalized, recent }
}

/**
 * Detects which strategy preset matches the current weights (or null if
 * custom). Delegates the matching itself to the shared sticky-preset module
 * (also used by detectContentWindowPreset in vespa-content-window.tsx) — this
 * wrapper only supplies STRATEGY_PRESETS' weights and the domain-specific
 * equality check (exact personalized/recent match), and translates the
 * shared CUSTOM_PRESET sentinel back to `null` to preserve this function's
 * existing return type/call sites (`detectPreset(weights) ?? CUSTOM_STRATEGY`).
 */
export function detectPreset(weights: RankingWeights): StrategyPreset | null {
    const presetWeights = Object.fromEntries(
        Object.entries(STRATEGY_PRESETS).map(([key, preset]) => [key, preset.weights]),
    ) as Record<StrategyPreset, RankingWeights>

    const match = detectPresetMatch(
        presetWeights,
        weights,
        (preset, current) => preset.personalized === current.personalized && preset.recent === current.recent,
    )

    return match === CUSTOM_PRESET ? null : match
}

/**
 * Returns the card label for a Vespa configuration. Vespa configurations are
 * independent parallel scorers — every configuration scores the entire
 * eligible pool simultaneously and the reader receives whichever article
 * scores highest across all of them — so the label carries no ordinal/priority
 * connotation.
 */
export function getConfigCardLabels(): { label: string } {
    return { label: 'Configuration' }
}

/**
 * Maps a PQueryBuilder commit to the filters value to persist:
 * - more than `maxFilters` conditions -> skip (over the cap; VespaFilterCountGuard
 *   reverts the store, which re-commits a within-limit document)
 * - empty root ({ and: [] }) -> null (the app-wide "no filters" convention)
 * - otherwise -> the document unchanged
 *
 * Pure so the persistence decision can be unit-tested without rendering the
 * builder.
 */
export function normalizePQueryCommit(
    value: Record<string, unknown>,
    maxFilters: number,
): { skip: true } | { skip: false; filters: PQueryDocument | null } {
    const andGroup = value.and as unknown[]

    if (Array.isArray(andGroup) && andGroup.length > maxFilters) {
        return { skip: true }
    }

    const isEmpty = Array.isArray(andGroup) && andGroup.length === 0
    return { skip: false, filters: isEmpty ? null : (value as PQueryDocument) }
}

/**
 * Enforces MAX_VESPA_FILTERS against the PQueryBuilder's live store, rendered
 * as a child of PQueryBuilderProvider so it can use usePQueryBuilder() for
 * imperative access.
 *
 * PQueryBuilder has no built-in max-conditions constraint, and by the time
 * onRuleCommit fires the store has already added the (N+1)th condition — so
 * a purely reactive "reject the commit, don't persist" guard (the original
 * approach) leaves a phantom row visible in the builder that was never saved,
 * with no indication to the user which row it was. This component instead
 * watches conditionCount reactively and, when it exceeds the limit, removes
 * the excess condition directly from the store via pqb.removeCondition — so
 * the visual state and the persisted state can never disagree.
 *
 * getChildren(getRootId()) returns condition IDs in insertion order, so the
 * excess conditions removed are always the most recently added ones — the
 * ones the user just tried to add past the cap, not an arbitrary row.
 */
export const VespaFilterCountGuard: FC<{ maxConditions: number }> = ({ maxConditions }) => {
    const pqb = usePQueryBuilder()

    useEffect(() => {
        if (pqb.conditionCount <= maxConditions) return

        simpleNotification('error', `You can add up to ${maxConditions} conditions per configuration.`)

        const rootChildren = pqb.getChildren(pqb.getRootId())
        const excessCount = pqb.conditionCount - maxConditions
        const excessIds = rootChildren.slice(rootChildren.length - excessCount)
        excessIds.forEach((id) => pqb.removeCondition(id))
    }, [pqb.conditionCount, maxConditions]) // eslint-disable-line react-hooks/exhaustive-deps

    return null
}

export const VespaConfigurationEditor: FC<VespaConfigurationEditorProps> = ({ configuration, handleAdd }) => {
    const { recordId, configuration: config } = configuration

    const viewCtx = useContext(EditableViewContext)
    const isReadonly = viewCtx.mode === 'view'

    const recommendationContext = useContext(VespaRecommendedNotificationContext)
    const recommendationDispatch = useContext(VespaRecommendedNotificationDispatchContext)

    // Derive UI state from stored config
    const [weights, setWeights] = useState<RankingWeights>(() => profileToWeights(config))
    const [contentWindow, setContentWindow] = useState<ContentWindow>(() =>
        secondsToContentWindow(config.freshness_max_age?.intervalSeconds ?? null),
    )
    // Explicit selection so "Custom" can be chosen (and stick) even when the
    // current weights happen to match a preset — see handleStrategySelect.
    const [selectedStrategy, setSelectedStrategy] = useState<StrategySelection>(
        () => detectPreset(profileToWeights(config)) ?? CUSTOM_STRATEGY,
    )
    // Mirrors selectedStrategy — explicit selection so "Custom" can be chosen
    // (and stick) for the Content Window presets even when the current
    // value/unit happens to match one, same rationale as above.
    const [selectedContentWindow, setSelectedContentWindow] = useState<ContentWindowSelection>(() =>
        detectContentWindowPreset(secondsToContentWindow(config.freshness_max_age?.intervalSeconds ?? null)),
    )

    const updateConfig = (fields: Partial<PropertiesOf<VespaRecommendationConfiguration>>) => {
        recommendationDispatch({
            type: 'update-configuration',
            recordId,
            configuration: VespaRecommendationConfiguration.from({
                id: config.id,
                ranking_profile: config.ranking_profile,
                count: config.count,
                freshness_max_age: config.freshness_max_age ?? null,
                freshness_half_response: config.freshness_half_response ?? null,
                filters: config.filters ?? null,
                personalized_weight: config.personalized_weight ?? null,
                recent_weight: config.recent_weight ?? null,
                ...fields,
            }),
        })
    }

    /**
     * Persists dial weights as the real personalized_weight/recent_weight
     * query coefficients (not the legacy ranking_profile proxy — see the
     * weightsToProfile note). freshness_half_response is intentionally left
     * untouched going forward: it's a separate, still-valid knob (per-config
     * freshness decay override) that just happens to not be wired to this
     * dial anymore.
     */
    const persistWeights = (newWeights: RankingWeights) => {
        updateConfig({
            ranking_profile: weightsToProfile(newWeights),
            personalized_weight: dialStopToWeight(newWeights.personalized),
            recent_weight: dialStopToWeight(newWeights.recent),
        })
    }

    const handleStrategySelect = (selection: StrategySelection) => {
        if (selection === CUSTOM_STRATEGY) {
            // Custom retains the current weights and switches the selector into
            // hand-tuning mode; nothing to persist since the weights are unchanged.
            setSelectedStrategy(CUSTOM_STRATEGY)
            return
        }

        const newWeights = STRATEGY_PRESETS[selection].weights
        setWeights(newWeights)
        setSelectedStrategy(selection)
        persistWeights(newWeights)
    }

    const handleWeightsChange = (newWeights: RankingWeights) => {
        setWeights(newWeights)
        // Hand-tuning the dials re-derives the selection: snaps to a named preset
        // when the weights match one, otherwise reflects Custom.
        setSelectedStrategy(detectPreset(newWeights) ?? CUSTOM_STRATEGY)
        persistWeights(newWeights)
    }

    const handleContentWindowChange = (win: ContentWindow, selection: ContentWindowSelection) => {
        setContentWindow(win)
        setSelectedContentWindow(selection)
        const seconds = contentWindowToSeconds(win)
        updateConfig({
            freshness_max_age: seconds
                ? RelativeDate.from({
                      intervalSeconds: seconds,
                      displayMetric:
                          win.unit === 'hours' ? RelativeDateDisplayMetric.HOURS : RelativeDateDisplayMetric.DAYS,
                  })
                : null,
        })
    }

    /**
     * Self-heals legacy configs saved with freshness_max_age: null (from before
     * a Content Window was a required field). Every config created today
     * (defaultVespaConfigurationValue, and the equivalent inline default in
     * vespa-recommendation-editor.tsx) already has a real freshness_max_age,
     * so this effect only ever fires for configs saved before that default
     * existed — never for a config created in the current session.
     * VespaContentWindow always displays a concrete window
     * (secondsToContentWindow defaults nulls to 12 hours), so without this
     * effect a legacy null config would visually show "12 hours" on load
     * while the stored value stayed null — display and persistence
     * disagreeing until the user happened to touch this field. Runs once on
     * mount per config and persists the same default/selection the UI is
     * already showing, so what's displayed and what's saved never diverge.
     */
    useEffect(() => {
        if (config.freshness_max_age == null) {
            handleContentWindowChange(contentWindow, selectedContentWindow)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const handleFiltersChange = (pquery: PQueryDocument | null) => {
        updateConfig({ filters: pquery })
    }

    /**
     * PQueryBuilder's onRuleCommit fires the full serialized ConditionGroup on
     * every add/edit/delete. An empty root ({ and: [] }) means no conditions —
     * normalize that to null to match the rest of the app's null-means-no-filters
     * convention.
     *
     * PQueryBuilder has no built-in max-conditions constraint, so MAX_VESPA_FILTERS
     * is enforced here (see normalizePQueryCommit). This handler only guards
     * persistence (skips saving a commit that's over the limit) — reverting the
     * phantom (N+1)th row from the builder's own visual state is handled
     * separately by VespaFilterCountGuard, which has imperative access to the
     * store via usePQueryBuilder() (this handler doesn't, since it's not a
     * descendant of the PQueryBuilderProvider it's passed to). The guard's own
     * revert fires a corrected commit back through this same handler, which then
     * persists normally once back under the limit.
     */
    const handlePQueryBuilderCommit = (value: Record<string, unknown>) => {
        const result = normalizePQueryCommit(value, MAX_VESPA_FILTERS)
        if (result.skip) return
        handleFiltersChange(result.filters)
    }

    // Index off recordId — the Map's own stable key — not config.id. config.id
    // is the display ordinal ('first'/'second'/...) that the reducer rewrites
    // on every remove/shift, so it's a label, not an identity.
    const configs = Array.from(recommendationContext.configs.values())
    const configIndex = Array.from(recommendationContext.configs.keys()).indexOf(recordId)
    const ordinal = configIndex + 1
    const { label: cardLabel } = getConfigCardLabels()

    // Restrict available fields to those with operators valid for this mode.
    //
    // Memoized (not recomputed inline) because PQueryBuilder/PQueryBuilderProvider
    // re-initializes its internal store whenever the `fields` prop reference
    // changes (see pquery-builder-provider.tsx's re-init effect, dependent on
    // `fields`). An inline .map()/.filter() produces a new array on every render,
    // which triggered store.reinitialize() -> onRuleCommit emission -> updateConfig()
    // dispatch -> new context value -> re-render -> new fieldsForMode reference ->
    // reinitialize again, an infinite loop (visible as rapid flashing on hover,
    // since hover-triggered re-renders landed on the same cycle). VESPA_FILTER_FIELDS
    // and the operator sets are module-level constants, so this only needs to
    // recompute if the sets it depends on ever become props/state.
    const fieldsForMode: PQueryBuilderField[] = useMemo(() => {
        const operatorSet = new Set([...INCLUSION_OPERATORS, ...EXCLUSION_OPERATORS])
        return VESPA_FILTER_FIELDS.map((field) => ({
            ...field,
            operators: field.operators?.filter((op) => operatorSet.has(op)),
        })).filter((f) => f.operators && f.operators.length > 0)
    }, [])

    return (
        <VespaConfigurationCard
            recordId={recordId}
            ordinal={ordinal}
            label={cardLabel}
            summary={generateSummaryText(weights, contentWindow, config.filters)}
            readonly={isReadonly}
            isFirst={configIndex === 0}
            isLast={configIndex === configs.length - 1}
            onShift={(direction) =>
                recommendationDispatch({ type: 'shift-configuration', recordId, ordinalShift: direction })
            }
            onCopy={() => {
                let canDuplicate = false
                try {
                    canDuplicate = !!config.ranking_profile
                    if (!canDuplicate) {
                        throw new Error('A configuration must have a ranking profile selected to be duplicated')
                    }
                } catch (error) {
                    simpleNotification('error', error.message)
                }

                if (canDuplicate) {
                    handleAdd(
                        VespaRecommendationConfiguration.from({
                            id: config.id,
                            ranking_profile: config.ranking_profile,
                            count: config.count,
                            freshness_max_age: config.freshness_max_age ?? null,
                            freshness_half_response: config.freshness_half_response ?? null,
                            filters: config.filters ?? null,
                            personalized_weight: config.personalized_weight ?? null,
                            recent_weight: config.recent_weight ?? null,
                        }),
                    )
                }
            }}
            onDelete={() => recommendationDispatch({ type: 'remove-configuration', recordId })}
        >
            {/* ─── NLP SUMMARY ─────────────────────────────────── */}
            {/*
                Untitled Well.Section (no `title` prop) — Alert now owns the
                visible "Recommendation Summary" title via its `message` prop,
                so a second title above it would be redundant. The wrapper
                itself is still required though: Well's auto-divider/spacing
                system is a CSS adjacency rule keyed off .aqe-well-section
                (sibling + sibling), so dropping the wrapper entirely wouldn't
                just remove this section's own divider — it would also break
                the divider/spacing between this box and "How to Rank" below,
                since that rule requires the immediately preceding sibling to
                also be a Well.Section.
            */}
            <Well.Section>
                <VespaConfigSummary weights={weights} contentWindow={contentWindow} filters={config.filters} />
            </Well.Section>

            {/* ─── HOW TO RANK ─────────────────────────────────── */}
            <Well.Section title="How to Rank">
                <Form layout="vertical" colon={false}>
                    <Form.Item label="Strategy">
                        <VespaStrategyPresets
                            active={selectedStrategy}
                            onChange={handleStrategySelect}
                            readonly={isReadonly}
                        />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span>
                                Ranking Weights{' '}
                                <Popover content="How much each signal counts when ordering the eligible content.">
                                    <InfoCircleOutlined className="info-icon" />
                                </Popover>
                            </span>
                        }
                    >
                        <VespaRankingWeights weights={weights} onChange={handleWeightsChange} readonly={isReadonly} />
                    </Form.Item>
                </Form>
            </Well.Section>

            {/* ─── WHAT TO RECOMMEND ───────────────────────────── */}
            <Well.Section
                title={
                    <span>
                        Content Window{' '}
                        <Popover content="Only content published in this window is eligible.">
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </span>
                }
            >
                <Form layout="vertical" colon={false}>
                    <Form.Item label={null}>
                        <VespaContentWindow
                            window={contentWindow}
                            activeSelection={selectedContentWindow}
                            onChange={handleContentWindowChange}
                            readonly={isReadonly}
                        />
                    </Form.Item>
                </Form>
            </Well.Section>

            <Well.Section title="What to Recommend">
                {/*
                    Explicit PQueryBuilderProvider (rather than the standalone
                    <PQueryBuilder value onRuleCommit>, which auto-wraps its own
                    provider) so VespaFilterCountGuard can get imperative store
                    access via usePQueryBuilder() to revert over-the-limit
                    conditions — see MAX_VESPA_FILTERS enforcement note above.
                */}
                <PQueryBuilderProvider
                    // recordId, not config.id — the reducer rewrites config.id
                    // on every reorder, which would remount the builder (and
                    // regenerate every condition node id) on each shift.
                    key={recordId}
                    value={config.filters ?? undefined}
                    fields={fieldsForMode}
                    labels={{
                        groupHeaders: {
                            and: null,
                        },
                        emptyState: 'No conditions yet. Recommending all eligible content.',
                    }}
                    onRuleCommit={handlePQueryBuilderCommit}
                    onUnsupported={() => {
                        // Vespa filters are always a flat AND group (maxDepth=1,
                        // allowedGroupTypes=[]), so this should be unreachable —
                        // surfaced defensively in case a legacy/malformed filters
                        // document (e.g. an OR group or NestedQuery) somehow saved.
                        simpleNotification(
                            'error',
                            'This configuration has an unsupported filter structure and cannot be edited here.',
                        )
                    }}
                    constraints={{
                        maxDepth: 1,
                        allowedGroupTypes: [],
                    }}
                >
                    <PQueryBuilder disabled={isReadonly} />
                    <VespaFilterCountGuard maxConditions={MAX_VESPA_FILTERS} />
                </PQueryBuilderProvider>
            </Well.Section>
        </VespaConfigurationCard>
    )
}
